import { useCallback } from 'react';
import { FilterType } from '@repo/frontend-types';

// Context
import {
  useEventContext,
  useFilterContext,
  useSpotlightStore,
  useTwinContext,
} from '@repo/front-end-state';
// Utils
import { flyToTarget, FlightInterrupted } from '@repo/ui';

/**
 * "Show whole twin" (ops-clear-selection FR1): drop the current pick and fly back to the twin's
 * start view, without leaving the mode the user is in.
 *
 * Clears ONLY the pick — the ENTITY and ASSET filters, the same pair `useSpotlight` swaps — and
 * leaves DATE/TIME and `appMode` exactly as they were.
 *
 * ⚠️ NOT `clearFilter()`. That empties every filter including DATE and TIME and, when nothing but
 * DATE/TIME was left, switches the mode to TWIN_SELECTION or LIVE — which would throw a wall display
 * out of Operations, the one place this action exists for.
 *
 * Also clears the spotlight BID (without bumping the fire count, so re-picking the same equipment
 * from the dashboard still fires) and the scene hover highlight, so nothing stays lit.
 */
export const useResetTwinView = () => {
  const { removeFilterByType } = useFilterContext();
  const { setCurrentHoveredValue } = useEventContext();
  const { twin, twinBId } = useTwinContext();
  // While a cross-twin spotlight is switching gyms, useSpotlight has already dropped the loaded
  // twin but holds the target in `twinBId`. Falling back to it keeps a reset during that switch
  // from being a no-op that the spotlight's queued phase-2 flight would then override.
  const twinBid = twin?.adminTwin?.bid ?? twinBId ?? undefined;

  const resetToWholeTwin = useCallback(() => {
    if (!twinBid) return;

    removeFilterByType(FilterType.ENTITY);
    removeFilterByType(FilterType.ASSET);
    useSpotlightStore.getState().clearSpotlight();
    setCurrentHoveredValue(null);

    // flyToTarget cancels any flight already running, and that cancellation rejects with
    // FlightInterrupted, which is expected, not an error. A spotlight's queued phase-2 flight
    // would still fire after its phase 1 is cancelled — useSpotlight drops it because the
    // spotlight cleared above is no longer current (chainAfterPredictiveFlight).
    flyToTarget(twinBid).catch((err: unknown) => {
      if (err instanceof FlightInterrupted) return;
      // eslint-disable-next-line no-console
      console.error('[useResetTwinView] flyToTarget failed', err);
    });
  }, [twinBid, removeFilterByType, setCurrentHoveredValue]);

  return resetToWholeTwin;
};
